import React, { useMemo } from 'react';
import Select, { MultiValue, StylesConfig } from 'react-select';
import { useDarkMode } from '../../hooks/useDarkMode';
import { getSelectStyles } from './selectStyles';

interface Option {
  value: string;
  label: string;
}

interface SearchableMultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  allLabel?: string;
  disabled?: boolean;
  className?: string;
}

export const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select items',
  allLabel = 'All',
  disabled = false,
  className = '',
}) => {
  const { isDarkMode } = useDarkMode();

  // Handle "All" option: empty array means all selected (only if allLabel is provided)
  const hasAllOption = allLabel && allLabel.trim() !== '';
  const isAllSelected = hasAllOption && value.length === 0;
  const allOption: Option | null = useMemo(
    () => hasAllOption ? { value: '__all__', label: allLabel } : null,
    [hasAllOption, allLabel]
  );

  // Add "All" option at the beginning if provided
  const optionsWithAll = allOption ? [allOption, ...options] : options;

  const selectedOptions = useMemo(
    () =>
      isAllSelected && allOption
        ? [allOption]
        : options.filter((opt) => value.includes(opt.value)),
    [isAllSelected, allOption, options, value]
  );

  const handleChange = (newValue: MultiValue<Option>) => {
    if (!newValue || newValue.length === 0) {
      onChange(hasAllOption ? [] : []);
      return;
    }

    // Check if "All" is in the new selection
    const allInSelection =
      hasAllOption && newValue.some((opt) => opt.value === '__all__');

    // Get all non-"All" options from the new selection
    const regularOptions = newValue.filter((opt) => opt.value !== '__all__');

    // If we're currently in "All" state (empty array) and user is selecting individual items
    const wasAllSelected = hasAllOption && value.length === 0;

    if (allInSelection && regularOptions.length > 0) {
      // Both "All" and individual items are selected - remove "All" and keep only individual items
      const selectedIds = regularOptions.map((opt) => opt.value);
      onChange(selectedIds);
    } else if (allInSelection) {
      // Only "All" is selected
      onChange([]);
    } else if (wasAllSelected && regularOptions.length > 0) {
      // Transitioning from "All" to individual selection
      const selectedIds = regularOptions.map((opt) => opt.value);
      onChange(selectedIds);
    } else {
      // Normal individual selection
      const selectedIds = regularOptions.map((opt) => opt.value);

      // If all items are selected, automatically switch to "All" (empty array)
      if (hasAllOption && selectedIds.length === options.length) {
        onChange([]);
      } else {
        onChange(selectedIds);
      }
    }
  };

  const formatOptionLabel = ({ label, value: optValue }: Option) => {
    if (hasAllOption && optValue === '__all__') {
      return <span className="font-medium">{label}</span>;
    }
    return <span>{label}</span>;
  };

  // Get base styles and customize for multi-select
  const baseStyles = getSelectStyles<Option, true>(isDarkMode, 'compact');
  const customStyles: StylesConfig<Option, true> = {
    ...baseStyles,
    control: (base, state) => ({
      ...(baseStyles.control?.(base, state) || base),
      maxHeight: '78px',
      overflow: 'hidden',
    }),
    valueContainer: (base) => ({
      ...base,
      maxHeight: '72px',
      overflowY: 'auto',
      overflowX: 'hidden',
      flexWrap: 'wrap',
      gap: '4px',
    }),
    option: (base, state) => ({
      ...(baseStyles.option?.(base, state) || base),
      fontWeight: state.data?.value === '__all__' ? '500' : 'normal',
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
      maxHeight: '300px',
      overflowY: 'auto',
    }),
  };

  return (
    <div className={className}>
      <Select<Option, true>
        options={optionsWithAll}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={disabled}
        isClearable={false}
        isSearchable
        isMulti
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        formatOptionLabel={formatOptionLabel}
        noOptionsMessage={() => 'No options found'}
        styles={customStyles}
        className="react-select-container"
        classNamePrefix="react-select"
      />
    </div>
  );
};
